import {
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Menu } from 'lucide-react';
import ConfirmationModal from './ConfirmationModal';
import ProfileModal from './ProfileModal';
import NotificationBell from './NotificationBell';
import NotificationModal from './NotificationModal';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth, useUserRole, useCustomerData } from '../../hooks/useAuth';
import axiosInstance from '../../services/axiosInstance';
import { authStorage } from '../../utils/authStorage';

export default function Header({ allowedRoles = [], toggleSidebar }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const { notifications, markAsRead, markAllAsRead } = useNotifications();
  const dropdownRef = useRef();
  const { user, logout, customerId } = useAuth();
  const role = useUserRole();
  const getCustomerData = useCustomerData();
  // Notification logic is now handled by NotificationContext

  // Get customer info for admin, POS, and cashier
  const customer = (role === 1 || role === 2) && customerId ? getCustomerData() : null;

  // Keep logo in state so it can update when superadmin changes it
  const [logoUrl, setLogoUrl] = useState(customer?.logo || null);

  // Map numeric user_type to role label
  const roleLabels = { 0: 'Super Admin', 1: 'Admin' };
  const displayName = user?.username || 'Unknown User';
  const roleLabel = user?.user_type === 2
    ? (user?.role_name || 'User')
    : (roleLabels[user?.user_type] || 'Unknown Role');
  const normalizeImageUrl = useCallback((url, updatedAt) => {
    if (!url) return null;
    const raw = String(url).trim();
    const withBase = /^https?:\/\//i.test(raw)
      ? raw
      : new URL(raw.startsWith('/') ? raw : `/${raw}`, process.env.REACT_APP_API_URL || window.location.origin).toString();
    const sep = withBase.includes('?') ? '&' : '?';
    const v = updatedAt ? new Date(updatedAt).getTime() : Date.now();
    return `${withBase}${sep}v=${v}`;
  }, []);
  const [profileImageUrl, setProfileImageUrl] = useState(
    normalizeImageUrl(user?.image || user?.picture, user?.updated_at)
  );

  // Logout handler
  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  // Short helper: fetch latest customer data and update logo/cache for Admins
  const refreshCustomerLogo = useCallback(async () => {
    if (role !== 1 || !customerId) return;

    const withCacheBuster = (url, updatedAt) => {
      if (!url) return url;
      const sep = url.includes("?") ? "&" : "?";
      const v = updatedAt ? new Date(updatedAt).getTime() : Date.now();
      return `${url}${sep}v=${v}`;
    };

    try {
      const res = await axiosInstance.get("/superadmin/customers");
      const list = Array.isArray(res.data) ? res.data : (res.data?.customers || []);
      const found = list.find((c) => String(c.id) === String(customerId));
      if (found) {
        // Use authStorage.setCustomerData to ensure only one customer_* key exists
        authStorage.setCustomerData(customerId, found);
        setLogoUrl(found.logo ? withCacheBuster(found.logo, found.updated_at) : null);
      }
    } catch (e) {
      console.debug("Header: failed to refresh customer logo", e?.message || e);
    }
  }, [role, customerId]);

  // Fetch latest user profile to ensure header avatar matches Profile section.
  const refreshUserProfileImage = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axiosInstance.get(`/admin/users/${user.id}`);
      const profile = res.data?.data || res.data || {};
      const hasProfilePictureField = Object.prototype.hasOwnProperty.call(profile, "picture");
      const hasProfileImageField = Object.prototype.hasOwnProperty.call(profile, "image");
      const freshImage = hasProfilePictureField
        ? profile.picture
        : hasProfileImageField
          ? profile.image
          : (user?.picture ?? user?.image ?? null);
      setProfileImageUrl(normalizeImageUrl(freshImage, profile.updated_at || user.updated_at));
    } catch (e) {
      setProfileImageUrl(normalizeImageUrl(user?.image || user?.picture, user?.updated_at));
      console.debug('Header: failed to refresh profile image', e?.message || e);
    }
  }, [user, normalizeImageUrl]);

  // Redirect if user not logged in or role not allowed
  useEffect(() => {
    if (!user) {
      logout();
    } else if (allowedRoles.length && !allowedRoles.includes(user.user_type)) {
      alert('You are not authorized to access this page.');
      logout();
    }
  }, [user, allowedRoles, logout]);

  // Fetch fresh customer data (admins only) to reflect latest logo updates
  useEffect(() => {
    refreshCustomerLogo();
  }, [refreshCustomerLogo]);

  // Keep avatar synced with latest backend profile data.
  useEffect(() => {
    refreshUserProfileImage();
  }, [refreshUserProfileImage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Branding is shown in the Sidebar. Header stays clean with profile and actions only.
  const fallbackLogo = `${process.env.PUBLIC_URL || ""}/logo.png`;

  return (
    <>
      <header className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
        {/* Left section: Hamburger + Logo */}
        <div className="flex items-center gap-3 pl-2">
          {/* Hamburger icon: visible on mobile/tablet, minimalist style */}
          <button
            className="block lg:hidden p-2 rounded hover:bg-gray-100 focus:outline-none"
            aria-label="Open sidebar"
            onClick={toggleSidebar}
          >
            <Menu size={28} strokeWidth={2} className="text-gray-700" />
          </button>
          {/* Logo section */}
          {(role === 1 || role === 2) && (
            <>
              <div className="w-14 h-14 bg-white border-none rounded-lg flex items-center justify-center overflow-hidden shadow-none ml-1" style={{ background: 'transparent' }}>
                <img
                  src={logoUrl ? logoUrl : fallbackLogo}
                  alt={customer?.name ? `${customer.name} logo` : 'Default Logo'}
                  className="max-w-full max-h-full object-contain"
                  style={{ width: '100%', height: '100%', borderRadius: '0.5rem', boxShadow: 'none', background: 'transparent' }}
                />
              </div>
            </>
          )}
        </div>

        {/* Notification Bell and Profile - right side of header */}
        <div className="flex items-center gap-2">
          {/* Notifications - Admin only (role 1) */}
          {role === 1 && (
            <div className="relative">
              <NotificationBell count={notifications.filter(n => !n.read).length} onClick={() => setNotifDropdownOpen((prev) => !prev)} />
              {notifDropdownOpen && (
                <NotificationModal
                  open={notifDropdownOpen}
                  onClose={() => setNotifDropdownOpen(false)}
                  items={notifications}
                  onMarkAsRead={markAsRead}
                  onMarkAllAsRead={markAllAsRead}
                />
              )}
            </div>
          )}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown((prev) => !prev)}
              className="flex items-center gap-2 px-3 py-2 rounded-full :bg-gray-100hover transition"
            >
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt="Profile"
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <svg
                  viewBox="0 0 48 48"
                  className="w-8 h-8"
                  aria-label="Profile placeholder"
                >
                  <circle cx="24" cy="24" r="24" fill="#E5E7EB" />
                  <circle cx="24" cy="18" r="8" fill="#9CA3AF" />
                  <path d="M12 38c0-6.627 5.373-12 12-12s12 5.373 12 12" fill="#9CA3AF" />
                </svg>
              )}
              <span className="text-sm font-medium text-gray-800">
                {displayName} ({roleLabel})
              </span>
              <ChevronDownIcon className="w-4 h-4 text-gray-500" />
            </button>

            {showDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 z-50">
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    setShowProfileModal(true);
                  }}
                  className="flex items-center gap-2 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-100"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M6 20c0-3.314 2.686-6 6-6s6 2.686 6 6" fill="#9CA3AF" /></svg>
                  Profile
                </button>
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    setShowLogoutModal(true);
                  }}
                  className="flex items-center gap-2 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5 text-gray-500" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <ConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Yes"
        cancelText="No"
        type="delete"
        onKeyDown={e => {
          if (e.key === 'Enter') {
            handleLogout();
            setShowLogoutModal(false);
          }
        }}
      />

      {/* Profile Modal */}
      <ProfileModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
        displayName={displayName}
        roleLabel={roleLabel}
        profileImageUrl={profileImageUrl}
      />
    </>
  );
}
